import { useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import ReactPlayer from 'react-player'

import {
  Box,
  ChevronDownIcon,
  IconButton,
  PictureInPictureExitIcon,
  PictureInPictureIcon,
  Tooltip,
  expandableWrapperStyles,
  tokenVar,
} from '@hivewatch/dark'
import type { ColorScheme } from '@hivewatch/dark'

interface ShowVideoButtonIProps {
  show: boolean
  pipActive: boolean
  onClick: () => void
  enablePIP?: () => void
  disablePIP?: () => void
  url?: string
  colorScheme?: ColorScheme
}

/**
 * Rebrand (FeatureFlag.incidentDrawerRebrand) version of ShowVideoButton — the sticky
 * "Camera view" bar that appears when the incident video scrolls out of the drawer
 * viewport. Built entirely from dark-package components; the legacy Chakra v1 version
 * lives in ShowVideoButton.tsx and renders when the flag is off.
 */
export const ShowVideoButton = ({
  show,
  onClick,
  pipActive,
  enablePIP,
  disablePIP,
  url,
  colorScheme = 'dark',
}: ShowVideoButtonIProps) => {
  const { t } = useTranslation()
  const prefix = colorScheme === 'light' ? 'light.' : ''

  const [isHoveringPip, setIsHoveringPip] = useState(false)
  const [isHoveringScroll, setIsHoveringScroll] = useState(false)
  const pipButtonRef = useRef<HTMLDivElement>(null)
  const scrollButtonRef = useRef<HTMLDivElement>(null)

  const canPip = ReactPlayer.canEnablePIP(url ?? '')
  const isPipShown = canPip && pipActive
  // Product copy calls browser picture-in-picture "pop out video".
  const popOutLabel = isPipShown
    ? t('incidents:drawer_pop_out_video_exit')
    : t('incidents:drawer_pop_out_video')

  return (
    <>
      {/* The grid-rows collapse shared with CardExpandable gives the same
          slide-open behaviour as the legacy Chakra v1 <Collapse>. */}
      <Box {...expandableWrapperStyles(show)}>
        {/* While collapsed the buttons stay mounted, so hide them from keyboard
            and assistive tech. visibility transitions discretely — it flips to
            hidden only after the collapse animation finishes. */}
        <Box
          aria-hidden={!show}
          overflow='hidden'
          pointerEvents={show ? 'auto' : 'none'}
          transition='visibility 0.25s ease-in-out'
          visibility={show ? 'visible' : 'hidden'}
        >
          <Box
            alignItems='center'
            bg={`${prefix}bg.card`}
            borderBottom='1px solid'
            borderColor={`${prefix}interactive.tertiary-border`}
            display='flex'
            gap='8px'
            px='24px'
            width='100%'
          >
            <Box
              color={`${prefix}text.primary`}
              fontFamily="'Soehne Kraftig', sans-serif"
              fontSize='16px'
              fontWeight='500'
              lineHeight='20px'
              py='12px'
            >
              {t('incidents:drawer_camera_view_label')}
            </Box>
            <Box
              onMouseEnter={() => setIsHoveringPip(true)}
              onMouseLeave={() => setIsHoveringPip(false)}
              ref={pipButtonRef}
            >
              <IconButton
                aria-label={popOutLabel}
                colorScheme={colorScheme}
                dimension={30}
                icon={
                  isPipShown ? (
                    <PictureInPictureExitIcon size={18} />
                  ) : (
                    <PictureInPictureIcon size={18} />
                  )
                }
                onClick={isPipShown ? disablePIP : enablePIP}
              />
              {isHoveringPip && (
                <Tooltip
                  anchorEl={pipButtonRef.current}
                  colorScheme={colorScheme}
                  label={popOutLabel}
                  placement='bottom'
                />
              )}
            </Box>
            <Box
              marginLeft='auto'
              onMouseEnter={() => setIsHoveringScroll(true)}
              onMouseLeave={() => setIsHoveringScroll(false)}
              ref={scrollButtonRef}
            >
              <IconButton
                aria-label={t('incidents:drawer_scroll_to_video')}
                colorScheme={colorScheme}
                dimension={30}
                icon={
                  <ChevronDownIcon
                    color={tokenVar(
                      colorScheme === 'light'
                        ? 'colors.light.icon.secondary'
                        : 'colors.icon.secondary'
                    )}
                  />
                }
                onClick={() => {
                  onClick()
                  disablePIP?.()
                }}
              />
              {isHoveringScroll && (
                <Tooltip
                  anchorEl={scrollButtonRef.current}
                  colorScheme={colorScheme}
                  label={t('incidents:drawer_scroll_to_video')}
                  placement='bottom'
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  )
}
